// src/components/contact.js
import React, { useState } from 'react';
import './contact.css';
import { useTranslation } from './LanguageContext';
import emailjs from '@emailjs/browser';

// IDs/keys EmailJS via .env
const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

export default function Contact({ onSubmit }) {
  const { t } = useTranslation();

  const [form, setForm] = useState({
    civilite: '',
    prenom: '',
    nom: '',
    email: '',
    fonction: '',
    telephone: '',
    adresse: '',
    codePostal: '',
    pays: '',
    ville: '',
    message: ''
  });

  const [sending, setSending] = useState(false);

  const paysOptions = [
    'Maroc','France','Comores','Algérie','Tunisie','Sénégal',"Côte d’Ivoire",'Belgique','Suisse','Canada'
  ];

  const fonctionOptions = [
    'Dirigeant',
    'Directeur Financier (DAF)',
    'Comptable',
    'Auditeur',
    'Contrôleur de gestion',
    'Autres'
  ];

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }

  function formatResume(values) {
    // Résumé bien structuré pour l’email
    return [
      `Civilité : ${values.civilite || '-'}`,
      `Prénom : ${values.prenom || '-'}`,
      `Nom : ${values.nom || '-'}`,
      `Email : ${values.email || '-'}`,
      `Fonction : ${values.fonction || '-'}`,
      `Téléphone : ${values.telephone || '-'}`,
      `Adresse : ${values.adresse || '-'}`,
      `Code postal : ${values.codePostal || '-'}`,
      `Ville : ${values.ville || '-'}`,
      `Pays : ${values.pays || '-'}`,
      '',
      '— Message —',
      values.message || '-'
    ].join('\n');
  }

  async function handleSubmit(e) {
    e.preventDefault();

    // Validation minimale côté front (à adapter à vos besoins)
    if (!form.prenom || !form.nom || !form.email) {
      alert(t('contact.alert.missing'));
      return;
    }

    // Sanity check env EmailJS
    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      alert('EmailJS est mal configuré. Vérifiez SERVICE_ID, TEMPLATE_ID et PUBLIC_KEY dans votre .env');
      return;
    }

    setSending(true);
    try {
      const templateParams = {
        subject: 'Nouveau message du formulaire',
        ...form,
        resume: formatResume(form)
      };

      // Envoi via EmailJS (sans backend)
      await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, {
        publicKey: PUBLIC_KEY
      });

      // Enregistrement en base (non bloquant pour l'utilisateur)
      try {
        await fetch('/api/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form)
        });
      } catch (e) {
        // On ne bloque pas l'UX si la base échoue
        console.warn('Echec sauvegarde contact en base:', e);
      }

      alert(t('contact.alert.thanks'));

      // Callback optionnel (si un parent l’attend)
      if (typeof onSubmit === 'function') {
        onSubmit(form);
      }

      // Reset du formulaire
      setForm({
        civilite: '',
        prenom: '',
        nom: '',
        email: '',
        fonction: '',
        telephone: '',
        adresse: '',
        codePostal: '',
        pays: '',
        ville: '',
        message: ''
      });
    } catch (err) {
      console.error(err);
      alert(t('contact.alert.unable'));
    } finally {
      setSending(false);
    }
  }

  return (
    <section id="contact" className="section contact">
      <div className="container">
        <div className="section-title">
          <h2>{t('contact.title')}</h2>
        </div>

        <form className="contact-form" onSubmit={handleSubmit} noValidate>
          {/* Civilité */}
          <div className="form-field">
            <label htmlFor="civilite">{t('contact.civ')}</label>
            <select
              id="civilite"
              name="civilite"
              value={form.civilite}
              onChange={handleChange}
              required
              autoComplete="honorific-prefix"
            >
              <option value="" disabled>{t('contact.civ.choose')}</option>
              <option value="Monsieur">{t('contact.civ.mr')}</option>
              <option value="Madame">{t('contact.civ.ms')}</option>
            </select>
          </div>

          {/* Fonction */}
          <div className="form-field">
            <label htmlFor="fonction">Fonction</label>
            <select
              id="fonction"
              name="fonction"
              value={form.fonction}
              onChange={handleChange}
              required
              autoComplete="organization-title"
            >
              <option value="" disabled>Sélectionnez une fonction…</option>
              {fonctionOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          {/* Nom */}
          <div className="form-field">
            <label htmlFor="nom">Nom</label>
            <input
              id="nom"
              name="nom"
              type="text"
              placeholder="Ex : Garnier"
              value={form.nom}
              onChange={handleChange}
              required
              autoComplete="family-name"
            />
          </div>

          {/* Prénom */}
          <div className="form-field">
            <label htmlFor="prenom">Prénom</label>
            <input
              id="prenom"
              name="prenom"
              type="text"
              placeholder="Ex : Michel"
              value={form.prenom}
              onChange={handleChange}
              required
              autoComplete="given-name"
            />
          </div>

          {/* Email */}
          <div className="form-field">
            <label htmlFor="email">{t('contact.email')}</label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder={t('contact.email.placeholder')}
              value={form.email}
              onChange={handleChange}
              required
              autoComplete="email"
            />
          </div>

          {/* Téléphone */}
          <div className="form-field">
            <label htmlFor="telephone">{t('contact.phone')}</label>
            <input
              id="telephone"
              name="telephone"
              type="tel"
              placeholder={t('contact.phone.placeholder')}
              value={form.telephone}
              onChange={handleChange}
              required
              pattern="^\\+?[0-9\\s().-]{6,}$"
              autoComplete="tel"
              title={t('contact.phone.title')}
            />
          </div>

          {/* Adresse */}
          <div className="form-field">
            <label htmlFor="adresse">{t('contact.address')}</label>
            <input
              id="adresse"
              name="adresse"
              type="text"
              placeholder={t('contact.address.placeholder')}
              value={form.adresse}
              onChange={handleChange}
              required
              autoComplete="street-address"
            />
          </div>

          {/* Code postal */}
          <div className="form-field">
            <label htmlFor="codePostal">{t('contact.postcode')}</label>
            <input
              id="codePostal"
              name="codePostal"
              type="text"
              placeholder={t('contact.postcode.placeholder')}
              value={form.codePostal}
              onChange={handleChange}
              required
              autoComplete="postal-code"
              inputMode="numeric"
            />
          </div>

          {/* Pays */}
          <div className="form-field">
            <label htmlFor="pays">{t('contact.country')}</label>
            <select
              id="pays"
              name="pays"
              value={form.pays}
              onChange={handleChange}
              required
              autoComplete="country-name"
            >
              <option value="" disabled>{t('contact.country.choose')}</option>
              {paysOptions.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          {/* Ville */}
          <div className="form-field">
            <label htmlFor="ville">{t('contact.city')}</label>
            <input
              id="ville"
              name="ville"
              type="text"
              placeholder={t('contact.city.placeholder')}
              value={form.ville}
              onChange={handleChange}
              required
              autoComplete="address-level2"
            />
          </div>

          {/* Message */}
          <div className="form-field form-field--full">
            <label htmlFor="message">{t('contact.message')}</label>
            <textarea
              id="message"
              name="message"
              placeholder={t('contact.message.placeholder')}
              value={form.message}
              onChange={handleChange}
              rows={5}
              required
            />
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={sending}>
              {sending ? t('contact.sending') : t('contact.send')}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
// .env
